import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { usePageEditMode } from '@/context/PageEditModeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CREATE_FIELDS = [
  'name', 'description', 'category', 'base_price', 'images', 'thumbnail',
  'variants', 'sizes_available', 'colors_available', 'is_custom_order',
  'estimated_delivery_days', 'low_stock_threshold', 'featured', 'tags',
];

const pickPayload = (costume, overrides = {}) => {
  const out = {};
  for (const k of CREATE_FIELDS) {
    out[k] = overrides[k] !== undefined ? overrides[k] : costume[k];
  }
  if (out.base_price !== undefined && out.base_price !== null) {
    out.base_price = Number(out.base_price) || 0;
  }
  if (out.estimated_delivery_days !== undefined) {
    out.estimated_delivery_days = Number(out.estimated_delivery_days) || 14;
  }
  if (out.low_stock_threshold !== undefined) {
    out.low_stock_threshold = Number(out.low_stock_threshold) || 0;
  }
  for (const arrK of ['images', 'variants', 'sizes_available', 'colors_available', 'tags']) {
    if (out[arrK] == null) out[arrK] = [];
  }
  return out;
};

/**
 * Inline-editable costume image. In edit mode an upload overlay appears over
 * the image; clicking it opens the file picker. Uploads the chosen file,
 * replaces the thumbnail, and prepends to images[]. Clicks are swallowed so
 * the surrounding product Link does NOT navigate while editing.
 */
export const InlineCostumeImage = ({
  costume,
  src,
  alt = '',
  className = '',
  imgClassName = '',
  onSaved,
}) => {
  const { enabled } = usePageEditMode();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await axios.post(`${API}/costumes/admin/upload-image`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = up.data?.url;
      if (!newUrl) throw new Error('Upload returned no URL');

      const newImages = [newUrl, ...(costume.images || []).filter((u) => u !== newUrl)];
      const payload = pickPayload(costume, { thumbnail: newUrl, images: newImages });
      await axios.put(`${API}/costumes/admin/${costume.id}`, payload, { headers: authHeaders() });
      toast.success('Image updated');
      if (onSaved) onSaved({ ...costume, thumbnail: newUrl, images: newImages });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={className}>
      <img loading="lazy" decoding="async" src={src} alt={alt} className={imgClassName} />
      {enabled && (
        <>
          <button
            onClick={handlePick}
            disabled={uploading}
            data-edit-path={`costume.${costume?.id}.image`}
            className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 text-white opacity-100 hover:bg-black/85 transition-colors z-[5] cursor-pointer disabled:cursor-wait"
            data-testid={`costume-image-replace-${costume?.id}`}
          >
            <Upload className="w-5 h-5" />
            <span className="text-sm font-semibold">
              {uploading ? 'Uploading…' : 'Replace image'}
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};

export default InlineCostumeImage;
